import { useEffect, useState, useRef } from "react";
import {
  collection, query, where, getDocs, addDoc, deleteDoc, doc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuth } from "../../contexts/AuthContext";
import {
  Music, Trash2, Upload, CheckCircle, XCircle, Loader2, Radio, Clock, X, Plus, Library, Search,
} from "lucide-react";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatTime(seconds) {
  if (!seconds) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return h > 0 ? `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}` : `${m}:${s.toString().padStart(2, "0")}`;
}

function stripExt(name) {
  return name.replace(/\.[^/.]+$/, "").replace(/[_-]+/g, " ").trim();
}

export default function DashboardAutoDJ() {
  const { user } = useAuth();
  const [station, setStation] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Multi-file upload queue
  const [queue, setQueue] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  // Library tab
  const [addTab, setAddTab] = useState("upload"); // "upload" | "library"
  const [library, setLibrary] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");
  const [addedLibraryIds, setAddedLibraryIds] = useState(new Set());

  useEffect(() => { loadData(); }, [user]);

  const loadData = async () => {
    if (!user) return;
    try {
      const stQ = query(collection(db, "stations"), where("creatorId", "==", user.uid));
      const stSnap = await getDocs(stQ);
      if (stSnap.empty) { setLoading(false); return; }
      const st = { id: stSnap.docs[0].id, ...stSnap.docs[0].data() };
      setStation(st);
      const djSnap = await getDocs(collection(db, "stations", st.id, "autodj"));
      setTracks(djSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadLibrary = async () => {
    if (!user) return;
    setLibraryLoading(true);
    try {
      const q = query(collection(db, "audioUploads"), where("creatorId", "==", user.uid));
      const snap = await getDocs(q);
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (b.uploadedAt?.seconds || 0) - (a.uploadedAt?.seconds || 0));
      setLibrary(items);
    } catch (e) { console.error(e); }
    setLibraryLoading(false);
  };

  const switchToLibrary = () => {
    setAddTab("library");
    if (library.length === 0) loadLibrary();
  };

  const addFromLibrary = async (track) => {
    if (!station) return;
    const newTrack = {
      title: track.title || track.originalFilename || "Track",
      artist: track.artist || "",
      mp3FileUrl: track.mp3FileUrl,
      durationSeconds: track.durationSeconds || 0,
      createdAt: serverTimestamp(),
    };
    try {
      const ref = await addDoc(collection(db, "stations", station.id, "autodj"), newTrack);
      setTracks((t) => [...t, { id: ref.id, ...newTrack }]);
      setAddedLibraryIds((s) => new Set([...s, track.id]));
    } catch (e) { console.error("Library add error:", e); }
  };

  const filteredLibrary = library.filter((t) => {
    const q = librarySearch.toLowerCase();
    return !q || (t.title || "").toLowerCase().includes(q) || (t.artist || "").toLowerCase().includes(q);
  });

  const addFilesToQueue = (files) => {
    const items = Array.from(files)
      .filter((f) => f.type.startsWith("audio/") || /\.(mp3|wav|flac|ogg|aac|m4a)$/i.test(f.name))
      .map((f) => ({
        id: Math.random().toString(36).slice(2),
        file: f,
        title: stripExt(f.name),
        status: "pending", // pending | uploading | done | error
        error: null,
        duration: null,
      }));
    setQueue((q) => [...q, ...items]);
  };

  const handleFilePick = (e) => {
    if (e.target.files?.length) addFilesToQueue(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) addFilesToQueue(e.dataTransfer.files);
  };

  const removeFromQueue = (id) => setQueue((q) => q.filter((x) => x.id !== id));

  const updateTitle = (id, value) =>
    setQueue((q) => q.map((x) => x.id === id ? { ...x, title: value } : x));

  const uploadAll = async () => {
    const pending = queue.filter((item) => item.status === "pending");
    if (!pending.length || !station) return;
    setIsProcessing(true);

    const idToken = await user.getIdToken(true);

    for (const item of pending) {
      setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "uploading" } : x));
      try {
        const formData = new FormData();
        formData.append("file", item.file);
        formData.append("track_title", item.title.trim() || item.file.name);
        formData.append("show_id", "");

        const res = await fetch(`${API_URL}/api/audio/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}` },
          body: formData,
        });
        const text = await res.text();
        let data;
        try { data = JSON.parse(text); } catch { throw new Error(`Server error (${res.status})`); }
        if (!res.ok) throw new Error(data.detail || "Upload failed");

        const newTrack = {
          title: data.metadata?.title || item.title,
          artist: data.metadata?.artist || "",
          mp3FileUrl: data.downloadURL,
          durationSeconds: data.metadata?.durationSeconds || 0,
          createdAt: serverTimestamp(),
        };
        const djRef = await addDoc(collection(db, "stations", station.id, "autodj"), newTrack);
        setTracks((t) => [...t, { id: djRef.id, ...newTrack }]);
        setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "done", duration: newTrack.durationSeconds } : x));
      } catch (e) {
        setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "error", error: e.message } : x));
      }
    }
    setIsProcessing(false);
  };

  const handleDelete = async (trackId) => {
    if (!window.confirm("Remove this track from Auto-DJ?")) return;
    await deleteDoc(doc(db, "stations", station.id, "autodj", trackId));
    setTracks((t) => t.filter((x) => x.id !== trackId));
  };

  const totalRuntime = tracks.reduce((acc, t) => acc + (t.durationSeconds || 0), 0);
  const pendingCount = queue.filter((x) => x.status === "pending").length;
  const allDone = queue.length > 0 && queue.every((x) => x.status === "done" || x.status === "error");

  if (loading) return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-[#B026FF] border-t-transparent rounded-full animate-spin" /></div>;

  if (!station) {
    return (
      <div data-testid="autodj-no-station" className="text-center py-16 space-y-3">
        <Radio size={40} className="mx-auto text-zinc-700" />
        <h2 className="text-white font-bold text-base">No Station Yet</h2>
        <p className="text-zinc-500 text-sm">Create your station first, then add Auto-DJ tracks.</p>
        <Link to="/dashboard/station" className="inline-flex items-center gap-2 bg-[#B026FF] text-white font-bold text-sm px-5 py-2.5 rounded-full hover:bg-[#9b1ee6] transition-colors">
          Set Up Station
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-autodj" className="space-y-6 max-w-2xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Auto-DJ Tracks</h2>
          <p className="text-zinc-500 text-xs mt-0.5">
            Filler tracks for <span className="text-white">{station.name}</span> — play when no show is scheduled.
          </p>
        </div>
        {tracks.length > 0 && (
          <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
            <Clock size={12} />
            <span>{tracks.length} tracks · {formatTime(totalRuntime)}</span>
          </div>
        )}
      </div>

      {/* ── Add Tracks Card ─────────────────────────────── */}
      <div className="bg-[#111] border border-[#B026FF]/20 rounded-2xl overflow-hidden">
        {/* Tab header */}
        <div className="flex border-b border-white/5">
          <button
            onClick={() => setAddTab("upload")}
            className={`flex items-center gap-1.5 px-5 py-3 text-xs font-semibold transition-colors ${addTab === "upload" ? "text-[#B026FF] border-b-2 border-[#B026FF]" : "text-zinc-500 hover:text-zinc-300"}`}
          >
            <Upload size={11} /> Upload New
          </button>
          <button
            onClick={switchToLibrary}
            className={`flex items-center gap-1.5 px-5 py-3 text-xs font-semibold transition-colors ${addTab === "library" ? "text-[#B026FF] border-b-2 border-[#B026FF]" : "text-zinc-500 hover:text-zinc-300"}`}
          >
            <Library size={11} /> My Library
          </button>
          <div className="ml-auto flex items-center pr-4">
            <span className="text-zinc-600 text-xs">MP3, WAV, FLAC, OGG, AAC, M4A</span>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* ── Upload Tab ── */}
          {addTab === "upload" && (
            <>
              {/* Drop zone */}
              <div
                ref={dropRef}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="autodj-dropzone"
                className={`border-2 border-dashed rounded-xl px-4 py-8 text-center cursor-pointer transition-colors ${
                  dragging ? "border-[#B026FF]/60 bg-[#B026FF]/5" : "border-white/10 hover:border-[#B026FF]/30 hover:bg-[#B026FF]/5"
                }`}
              >
                <Upload size={22} className="mx-auto mb-2 text-zinc-600" />
                <p className="text-zinc-400 text-sm font-medium">Drop audio files here or click to browse</p>
                <p className="text-zinc-600 text-xs mt-1">Select multiple files at once to batch upload</p>
                <input ref={fileInputRef} type="file" accept="audio/*" multiple className="hidden" onChange={handleFilePick} />
              </div>

              {/* Queue list */}
              {queue.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-zinc-500 text-xs font-semibold uppercase tracking-widest">{queue.length} file{queue.length > 1 ? "s" : ""} selected</p>
                    {allDone && (
                      <button onClick={() => setQueue([])} className="text-zinc-600 text-xs hover:text-zinc-300 flex items-center gap-1">
                        <X size={11} /> Clear done
                      </button>
                    )}
                  </div>
                  <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                    {queue.map((item) => (
                      <div key={item.id} data-testid={`queue-item-${item.id}`}
                        className="flex items-center gap-2.5 bg-[#1a1a1a] rounded-xl px-3 py-2">
                        <span className="flex-shrink-0">
                          {item.status === "uploading" && <Loader2 size={14} className="text-[#B026FF] animate-spin" />}
                          {item.status === "done" && <CheckCircle size={14} className="text-[#00ff77]" />}
                          {item.status === "error" && <XCircle size={14} className="text-red-400" />}
                          {item.status === "pending" && <Music size={14} className="text-zinc-600" />}
                        </span>
                        <input
                          value={item.title}
                          onChange={(e) => updateTitle(item.id, e.target.value)}
                          disabled={item.status !== "pending"}
                          placeholder="Track title"
                          data-testid={`queue-title-${item.id}`}
                          className="flex-1 bg-transparent text-white text-xs focus:outline-none disabled:text-zinc-400 min-w-0"
                        />
                        {item.status === "done" && item.duration != null && (
                          <span className="text-zinc-500 text-xs flex-shrink-0">{formatTime(item.duration)}</span>
                        )}
                        {item.status === "error" && (
                          <span className="text-red-400 text-[10px] truncate max-w-[120px]" title={item.error}>{item.error}</span>
                        )}
                        {item.status === "uploading" && (
                          <span className="text-[#B026FF] text-[10px] flex-shrink-0">Converting…</span>
                        )}
                        {item.status === "pending" && (
                          <button onClick={() => removeFromQueue(item.id)} className="flex-shrink-0 text-zinc-600 hover:text-red-400 transition-colors">
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {pendingCount > 0 && (
                    <button
                      onClick={uploadAll}
                      disabled={isProcessing}
                      data-testid="upload-all-btn"
                      className="w-full flex items-center justify-center gap-2 bg-[#B026FF] text-white font-bold text-sm py-2.5 rounded-xl hover:bg-[#9b1ee6] transition-colors disabled:opacity-50"
                    >
                      {isProcessing ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> Upload {pendingCount} Track{pendingCount > 1 ? "s" : ""}</>}
                    </button>
                  )}
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 border border-white/10 text-zinc-400 text-sm py-2.5 rounded-xl hover:border-[#B026FF]/40 hover:text-white transition-colors"
              >
                <Plus size={14} /> Add {queue.length > 0 ? "more " : ""}files
              </button>
            </>
          )}

          {/* ── Library Tab ── */}
          {addTab === "library" && (
            <div className="space-y-3">
              <div className="relative">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  data-testid="library-search-autodj"
                  type="text"
                  placeholder="Search your uploaded tracks…"
                  value={librarySearch}
                  onChange={(e) => setLibrarySearch(e.target.value)}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl pl-8 pr-3 py-2 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#B026FF]/30"
                />
              </div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {libraryLoading && (
                  <div className="flex justify-center py-6">
                    <Loader2 size={18} className="animate-spin text-zinc-500" />
                  </div>
                )}
                {!libraryLoading && filteredLibrary.length === 0 && (
                  <div className="text-center py-8 text-zinc-600">
                    <Music size={24} className="mx-auto mb-2 opacity-40" />
                    <p className="text-xs">{library.length === 0 ? "No uploaded tracks yet." : "No tracks match your search."}</p>
                  </div>
                )}
                {filteredLibrary.map((track) => {
                  const alreadyAdded = addedLibraryIds.has(track.id);
                  return (
                    <div key={track.id} data-testid={`library-track-autodj-${track.id}`}
                      className="flex items-center gap-3 bg-[#1a1a1a] rounded-xl px-3 py-2.5">
                      <Music size={13} className="text-zinc-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{track.title || track.originalFilename}</p>
                        <p className="text-zinc-500 text-xs truncate">
                          {track.artist ? `${track.artist} · ` : ""}{formatTime(track.durationSeconds)}
                        </p>
                      </div>
                      <button
                        data-testid={`add-library-autodj-${track.id}`}
                        onClick={() => addFromLibrary(track)}
                        disabled={alreadyAdded}
                        className={`flex-shrink-0 flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${
                          alreadyAdded
                            ? "bg-[#00ff77]/10 text-[#00ff77] cursor-default"
                            : "bg-white/10 text-zinc-300 hover:bg-[#B026FF] hover:text-white"
                        }`}
                      >
                        {alreadyAdded ? "Added ✓" : <><Plus size={11} /> Add</>}
                      </button>
                    </div>
                  );
                })}
              </div>
              <p className="text-zinc-700 text-[10px] text-center">
                Tracks added from library are reused — no re-upload or re-encode needed
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Track List ─────────────────────────────── */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-3">
          Library ({tracks.length} tracks)
        </p>
        {tracks.length === 0 ? (
          <div className="text-center py-12 text-zinc-600">
            <Music size={28} className="mx-auto mb-2 opacity-40" />
            <p className="text-xs">No tracks yet. Upload some above.</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {tracks.map((track, i) => (
              <div key={track.id} data-testid={`autodj-track-${track.id}`}
                className="flex items-center gap-3 bg-[#111] border border-white/5 rounded-xl px-3 py-2.5">
                <span className="text-zinc-600 text-xs w-5 text-right flex-shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{track.title}</p>
                  {track.artist && <p className="text-zinc-500 text-xs truncate">{track.artist}</p>}
                </div>
                <span className="text-zinc-500 text-xs flex-shrink-0">{formatTime(track.durationSeconds)}</span>
                <a href={track.mp3FileUrl} target="_blank" rel="noopener noreferrer"
                  className="text-zinc-600 hover:text-[#00ff77] transition-colors flex-shrink-0" title="Preview">
                  <Music size={13} />
                </a>
                <button onClick={() => handleDelete(track.id)} data-testid={`delete-track-${track.id}`}
                  className="text-zinc-600 hover:text-red-400 transition-colors flex-shrink-0">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
